"use client";
import { useEffect } from "react";
import { type Translationtype } from "utils";

interface IModalProps {
  open: boolean;
  handleClose: VoidFunction;
  handleSave?: VoidFunction;
  title: string;
  saveBtnText?: string;
  lockBtn?: boolean;
  cancelBtnText?: string;
  children: React.ReactNode;
  t: Translationtype;
  showLoader?: boolean;
}

export function Modal({
  open,
  handleClose,
  handleSave,
  title,
  saveBtnText,
  cancelBtnText,
  lockBtn,
  children,
  t,
  showLoader,
}: IModalProps): JSX.Element {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        if (lockBtn) {
          // Prevent the default action if the form is not valid
          event.preventDefault();
        } else if (handleSave) {
          // Trigger the save action if the form is valid
          event.preventDefault();
          handleSave();
        }
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, lockBtn, handleSave]);

  return (
    <>
      {open ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto min-w-[300px] max-h-[90%] my-3 mx-auto max-w-[70%]">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full max-h-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-3 border-b border-solid border-slate-200 rounded-t flex-shrink-0">
                  <h3 className="text-2xl font-semibold">{title}</h3>
                </div>
                <div className="relative p-3 flex-auto overflow-y-auto">{children}</div>
                <div className="flex gap-3 items-center justify-end p-3 border-t border-solid border-slate-200 rounded-b flex-shrink-0">
                  {handleSave ?  (
                    <button
                      className={`${
                        lockBtn ? "flex items-center gap-2" : ""
                      } btn btn-icon`}
                      type="button"
                      disabled={lockBtn}
                      onClick={() => handleSave()}
                    >
                      {showLoader ? (
                        <span className="loading loading-spinner" />
                      ) : null}
                      {saveBtnText || t("Buttons.save")}
                    </button>
                  ) : null}
                  <button
                    className={`btn btn-outline btn-default ${
                      !handleSave ? "ml-auto" :  ""
                    }`}
                    onClick={() => handleClose()}
                    type="button"
                  >
                    {cancelBtnText || t("Buttons.close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black" />
        </>
      ) : null}
    </>
  );
}